import { Mui } from "@osu/react-ui";
import {
  forwardRef,
  Fragment,
  useEffect,
  useRef
} from "react";
import OpenFormsLinks from "../../Common/components/OpenFormsLinks";
import { useDispatch, useSelector } from "react-redux";
import { clearAlert, selectAlert, selectAlertIsError, selectIsAlertVisible } from "../../services/alert/slices";
import { ALERT_SEVERITY } from "../../util/constants";
import { selectRenderPaths } from "../../services/submissionPeriod/slices";

const ErrorMessage = ({ message }) => {
  if(!message) {
    return false
  }
  return  <Fragment>
    {message} If this message persists please contact the{" "}
    <Mui.Link href="mailto:oasis-support@lists.osu.edu">
      Web and Mobile Team
    </Mui.Link>.
  </Fragment>
}

const selectSeverity = (state, error) => {
  if(error) {
    return ALERT_SEVERITY.ERROR
  }
  const { severity: _severity } = selectAlert(state)
  const verifiedSeverity = Object.values(ALERT_SEVERITY).includes(_severity) ? _severity : ALERT_SEVERITY.INFO
  return verifiedSeverity
}

const selectTitle = state => {
  const alert = selectAlert(state)
  return alert?.title
}
const selectMessage = state => selectAlert(state)?.message

const Alert = forwardRef(
  (
    { onClose: onAlertClose },
    ref
  ) => {
    const alertShouldShow = useSelector(selectIsAlertVisible)
    const error = useSelector(selectAlertIsError)
    const title = useSelector(selectTitle)
    const message = useSelector(selectMessage)
    const severity = useSelector(selectSeverity)
    const dispatch = useDispatch()
    const timerRef = useRef();
    const showLinks = useSelector((state) => {
      return !error && alertShouldShow && selectRenderPaths(state);
    });
    const closeAlert = () => {
      dispatch(clearAlert())
    };

    const silentlyClose = (event) => {
      const isStillWithinParent =
        ref.current && ref.current.contains(event?.relatedTarget);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (!isStillWithinParent && !error) {
        timerRef.current = setTimeout(() => {
          closeAlert(event);
        }, 5000);
      }
    };

    const onClose = (event) => {
      closeAlert(event);
      onAlertClose && onAlertClose()
    };

    useEffect(() => {
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, []);

      useEffect(() => {        
        if (alertShouldShow && ref?.current) {
          const timeoutId = setTimeout(() => {
            ref.current.focus();
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);

          return () => clearTimeout(timeoutId);
        }
      }, [alertShouldShow, message, ref]);

      const handleAlertBlur = (event) => {
        const isNextTargetH1 = event?.relatedTarget?.tagName === 'H1'
        if(isNextTargetH1 && alertShouldShow && ref?.current) {
          ref.current.focus();
          return
        }

        silentlyClose();
      }

    const boxStyles = {
      marginBottom: 2,
      '&:focus': {
        outline: '2px solid',
        outlineColor: `${severity}.main`,
        outlineOffset: '2px',
        borderRadius: 1
      }
    }

    return (
      <Mui.Box
        ref={ref}
        tabIndex={-1}
        role="alert"
        aria-live={error ? "assertive" : "polite"}
        aria-atomic="true"
        sx={alertShouldShow ? boxStyles : undefined}
        onBlur={handleAlertBlur}
      >
        <Mui.Collapse in={alertShouldShow}>
          <Mui.Alert
            severity={severity}
            onClose={onClose}
            role="none"
          >
            {title && <Mui.AlertTitle>{title}</Mui.AlertTitle>}
            {error ? <ErrorMessage message={message} /> : (message || false)}
            {showLinks && (
              <Fragment>
                <Mui.Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                <section>
                  <Mui.Typography
                    sx={{ marginBottom: 1 }}
                    component="h2"
                    variant="h6"
                  >
                    Create New Form
                  </Mui.Typography>
                  <OpenFormsLinks onClick={onClose} color="inherit" />
                </section>
              </Fragment>
            )}
          </Mui.Alert>
        </Mui.Collapse>
      </Mui.Box>
    );
  }
);

export default Alert;
